import { scry, scryImageUrl } from "../lib/scryClient";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { auth, db } from "@/lib/firebase";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  getDocs,
  setDoc,
  serverTimestamp,
  getDoc,
  type DocumentData,
  type Query,
  type QuerySnapshot,
} from "firebase/firestore";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import EventCard, { EventDoc } from "@/events/EventCard";
import CreateEventDialog from "@/events/CreateEventDialog";
import { distanceMiles } from "@/lib/geoHash";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, LayoutGrid, Square as SingleCard } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { addFavorite, removeFavorite as removeFavoriteApi, toggleFavorite as toggleFavoriteApi } from "@/events/favorites";
import { roundRobinEvents } from "@/lib/eventsRoundRobin";
import SwipeDeck from "@/components/swipe/SwipeDeck";

/* --------------------------- Types & Small helpers --------------------------- */
type Raw = any;
type EventMap = Map<string, any>;

function scryfallImg(scryId?: string | null): string | null {
  if (!scryId) return null;
  return ` + scryImageUrl(scryId, "normal") + `;
}

function toNumber(v: any): number | null {
  return typeof v === "number" ? v : typeof v === "string" ? Number(v) : null;
}

/** Salvage a Date from a variety of possible fields */
function salvageStartDate(raw: any): Date | null {
  if (!raw) return null;
  const candidates: any[] = [
    raw.startTime,
    raw.start,
    raw.start_at,
    raw.startTimeMs,
    raw.startTimeMillis,
    raw.start_time_ms,
    raw.start_time,
    raw.startTimestamp,
    raw.start_timestamp,
  ];
  for (const c of candidates) {
    if (!c) continue;
    if (c instanceof Date) {
      if (!isNaN(c.getTime())) return c;
      continue;
    }
    if (c?.toDate) {
      try {
        const d = c.toDate();
        if (d instanceof Date && !isNaN(d.getTime())) return d;
      } catch {}
      continue;
    }
    if (c?.toMillis) {
      try {
        const d = new Date(c.toMillis());
        if (!isNaN(d.getTime())) return d;
      } catch {}
      continue;
    }
    if (typeof c === "number") {
      const d = new Date(c);
      if (!isNaN(d.getTime())) return d;
    } else if (typeof c === "string") {
      const t = Date.parse(c);
      if (Number.isFinite(t)) {
        const d = new Date(t);
        if (!isNaN(d.getTime())) return d;
      }
    }
  }
  return null;
}

/** Normalize shape for EventCard (safe fields + gallery) */
function normalizeForCard(raw: any) {
  if (!raw || !raw.id) return null;

  // Start time
  const rawStart = raw.startTime ?? raw.start ?? raw.start_at ?? null;
  let startTime: Date | null = null;
  if (rawStart?.toDate) {
    try { startTime = rawStart.toDate(); } catch {}
  } else if (rawStart?.toMillis) {
    try { startTime = new Date(rawStart.toMillis()); } catch {}
  } else if (rawStart instanceof Date) {
    startTime = isNaN(rawStart.getTime()) ? null : rawStart;
  } else if (typeof rawStart === "number") {
    const d = new Date(rawStart);
    startTime = isNaN(d.getTime()) ? null : d;
  } else if (typeof rawStart === "string") {
    const t = Date.parse(rawStart);
    if (Number.isFinite(t)) {
      const d = new Date(t);
      if (!isNaN(d.getTime())) startTime = d;
    }
  }
  if (!startTime) startTime = salvageStartDate(raw);

  const title = String(raw.title || "Event");

  // Location/address
  const loc =
    raw.location && Number.isFinite(raw.location.lat) && Number.isFinite(raw.location.lng)
      ? raw.location
      : raw.venue && Number.isFinite(raw.venue.lat) && Number.isFinite(raw.venue.lng)
      ? { lat: Number(raw.venue.lat), lng: Number(raw.venue.lng), name: raw.venue?.name ?? null }
      : null;
  const address = raw.address ?? raw.venue?.address ?? null;

  // Organizer
  const organizerUid = raw.event_host_uid || raw.organizerUid || raw.ownerUid || "";
  const organizerUsername = raw.organizerUsername || "";
  const organizerAvatarUrl = raw.organizerAvatarUrl || null;
  const visibility = raw.visibility || "public";

  // Gallery
  const rawImages = Array.isArray(raw.images) ? raw.images : [];
  const toUrl = (it: any): string | null => {
    if (!it) return null;
    if (typeof it === "string") return it;
    if (typeof it.url === "string") return it.url;
    if (typeof it.src === "string") return it.src;
    if (typeof it.image === "string") return it.image;
    return null;
  };
  const primaryUrl =
    toUrl(raw.coverImage) ||
    toUrl(raw.image) ||
    toUrl(rawImages[0]) ||
    scryfallImg(raw.scryfall_id) ||
    organizerAvatarUrl ||
    "/images/sg-card-back.png";
  const gallery: Array<{ id: string; src: string; alt?: string }> = [
    { id: "cover", src: String(primaryUrl), alt: title },
  ];
  for (let i = 0; i < rawImages.length; i++) {
    const u = toUrl(rawImages[i]);
    if (u && u !== primaryUrl) gallery.push({ id: `img-${i}`, src: u, alt: title });
  }
  const organizerAvatarFinal = organizerAvatarUrl || (visibility === "public" ? "/events/public.png" : "/events/private.png");

  return {
    ...raw,
    id: String(raw.id),
    title,
    startTime,
    location: raw.location ?? loc,
    address,
    organizerUid,
    organizerUsername,
    organizerAvatarUrl: organizerAvatarFinal,
    organizer: {
      id: String(organizerUid || ""),
      username: organizerUsername ? String(organizerUsername) : "",
      avatar_url: organizerAvatarFinal,
    },
    gallery,
    visibility,
  };
}

/* --------------------------------- Component -------------------------------- */
export default function EventsPage({
  readOnly = false,
  selectedDate = null,
  searchTerm = "",
  radiusMi,
  selectedFormats = [],
  visibilityFilter = "all",
  lgsFilter = "all",
  selectedProfileUid = null,
}: {
  readOnly?: boolean;
  selectedDate?: Date | null;
  searchTerm?: string;
  radiusMi?: number | null;
  selectedFormats?: string[];
  visibilityFilter?: "all" | "public" | "private";
  lgsFilter?: "all" | "only" | "hide";
  selectedProfileUid?: string | null;
} = {}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // View & Tab driven by URL params (so links are shareable)
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlView = (params.get("view") as "swipe" | "carousel" | "grid" | "calendar") || "swipe";
  const urlTab = (params.get("tab") as "all" | "favorites" | "archive" | "created") || "all";

  const [viewMode, setViewMode] = useState<"all" | "created" | "favorites" | "archived">(urlView);
  const [tab, setTab] = useState<"all" | "favorites" | "archive" | "created">(urlTab);

  useEffect(() => setViewMode(urlView), [urlView]);
  useEffect(() => setTab(urlTab), [urlTab]);

  function updateUrl(next: Partial<{ view: string; tab: string }>) {
    const p = new URLSearchParams(location.search);
    if (next.view) p.set("view", next.view);
    if (next.tab) p.set("tab", next.tab);
    navigate({ search: p.toString() }, { replace: true });
  }

  const [events, setEvents] = useState<any[]>([]);
  const [favorites, setFavorites] = useState<Record<string, boolean>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  // Local radius only if global not supplied
  const [radiusOverride, setRadiusOverride] = useState<number | null>(null);
  const effectiveRadius = radiusMi !== undefined ? radiusMi : radiusOverride;

  const [userLatLng, setUserLatLng] = useState<{ lat: number; lng: number } | null>(null);

  // Deck state (for carousel view)
  const [ordered, setOrdered] = useState<Raw[]>([]);
  const [idx, setIdx] = useState(0);
  const [viewed, setViewed] = useState<Set<string>>(new Set());

  // Grid/Carousel infinite scroll
  const [hasMore, setHasMore] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reduced motion
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduceMotion(!!mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // Get viewer location (best effort)
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserLatLng({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setUserLatLng(null),
      { enableHighAccuracy: true, maximumAge: 60_000 }
    );
  }, []);

  /* --------------------------------- Queries --------------------------------- */

  // Load IDs I follow (approved)
  async function fetchFollowedIds(meUid: string): Promise<string[]> {
    try {
      const qF = query(
        collection(db, "follows"),
        where("followerId", "==", meUid),
        where("status", "==", "approved"),
        limit(500)
      );
      const snap = await getDocs(qF);
      const ids: string[] = [];
      snap.forEach((d) => {
        const data: any = d.data();
        if (data.followingId && typeof data.followingId === "string") ids.push(data.followingId);
      });
      return ids;
    } catch (e) {
      console.warn("[events] fetchFollowedIds failed", e);
      return [];
    }
  }

  // Build queries for public, my private, and followed private events
  function buildEventQueries(meUid: string | null, followedIds: string[]): Query<DocumentData>[] {
    const colRef = collection(db, "events");
    const qPublic = query(colRef, where("visibility", "==", "public"), orderBy("startTime", "asc"), limit(500));
    const queries: Query<DocumentData>[] = [qPublic];

    if (meUid) {
      queries.push(
        query(colRef, where("visibility", "==", "private"), where("organizerUid", "==", meUid), orderBy("startTime", "asc"), limit(500))
      );
    }

    if (followedIds.length) {
      for (let i = 0; i < followedIds.length; i += 10) {
        const chunk = followedIds.slice(i, i + 10);
        if (chunk.length === 1) {
          queries.push(
            query(colRef, where("visibility", "==", "private"), where("organizerUid", "==", chunk[0]), orderBy("startTime", "asc"), limit(500))
          );
        } else {
          queries.push(
            query(colRef, where("visibility", "==", "private"), where("organizerUid", "in", chunk), orderBy("startTime", "asc"), limit(500))
          );
        }
      }
    }
    return queries;
  }

  function normalizeEvent(raw: any): any {
    if (!raw) return raw;
    const rawStart = raw.startTime ?? raw.start ?? raw.start_at ?? null;
    let startTime: Date | null = null;
    if (rawStart?.toDate) startTime = rawStart.toDate();
    else if (rawStart?.toMillis) startTime = new Date(rawStart.toMillis());
    else if (typeof rawStart === "number") startTime = new Date(rawStart);
    else if (typeof rawStart === "string") {
      const t = Date.parse(rawStart);
      startTime = Number.isFinite(t) ? new Date(t) : null;
    }
    const loc =
      raw.location && Number.isFinite(raw.location.lat) && Number.isFinite(raw.location.lng)
        ? raw.location
        : raw.venue && Number.isFinite(raw.venue.lat) && Number.isFinite(raw.venue.lng)
        ? { lat: Number(raw.venue.lat), lng: Number(raw.venue.lng), name: raw.venue.name ?? null }
        : null;

    const dist =
      userLatLng && loc
        ? distanceMiles(userLatLng.lat, userLatLng.lng, Number(loc.lat), Number(loc.lng))
        : null;
    return {
      ...raw,
      startTime,
      location: raw.location ?? loc,
      address: raw.address ?? raw.venue?.address ?? null,
      distanceMiles: dist,
    };
  }

  function commitEventsFromMap(map: EventMap) {
    const now = Date.now();
    const graceMs = 2 * 60 * 60 * 1000; // keep items that started in last 2h
    const all = Array.from(map.values()).map(normalizeEvent);
    const upcoming = all.filter((e) => {
      const ms = e.startTime?.getTime?.();
      return typeof ms === "number" ? ms >= now - graceMs : false;
    });
    let orderedList = upcoming;
    try {
      orderedList = roundRobinEvents(upcoming) || upcoming;
    } catch {}
    setEvents(orderedList);
    setOrdered((prev) => (prev.length === 0 ? orderedList : prev));
  }

  async function subscribeMultiQueries(meUid: string | null) {
    const followed = meUid ? await fetchFollowedIds(meUid) : [];
    const queries = buildEventQueries(meUid, followed);
    const map: EventMap = new Map();
    let active = true;

    const handleSnapshot = (snap: QuerySnapshot<DocumentData>) => {
      if (!active) return;
      snap.docChanges().forEach((chg) => {
        const id = chg.doc.id;
        if (chg.type === "removed") map.delete(id);
        else map.set(id, { id, ...chg.doc.data() });
      });
      commitEventsFromMap(map);
    };

    const unsubs = queries.map((qy) => onSnapshot(qy, handleSnapshot, (err) => console.warn("[events] sub query failed", err)));
    return () => {
      active = false;
      unsubs.forEach((u) => {
        try {
          u();
        } catch {}
      });
    };
  }

  async function fetchOnce(meUid: string | null) {
    const followed = meUid ? await fetchFollowedIds(meUid) : [];
    const queries = buildEventQueries(meUid, followed);
    const map: EventMap = new Map();
    await Promise.all(
      queries.map(async (qy) => {
        try {
          const snap = await getDocs(qy);
          snap.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
        } catch (e) {
          console.warn("[events] fetchOnce query failed", e);
        }
      })
    );
    commitEventsFromMap(map);
  }

  // Single effect to load/subscribe
  useEffect(() => {
    let unsub: (() => void) | null = null;
    const meUid = auth.currentUser?.uid || null;
    const USE_REALTIME = true;
    (async () => {
      if (USE_REALTIME) {
        unsub = await subscribeMultiQueries(meUid);
      } else {
        await fetchOnce(meUid);
      }
    })();
    return () => {
      if (unsub) unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser, userLatLng, refreshKey]);

  /* -------------------------------- Favorites -------------------------------- */

  type FavDoc = {
    id: string;
    eventId: string;
    createdAt?: any;
    updatedAt?: any;
    event: any;
    startTimeMs?: number | null;
  };

  const [favDocs, setFavDocs] = useState<FavDoc[]>([]);
  // Hydrated favorites (after fetching canonical events & computing startTimeMs)
  const [favResolved, setFavResolved] = useState<FavDoc[]>([]);

  // Live favorites subscription (raw)
  useEffect(() => {
    if (readOnly) return;
    const user = auth.currentUser;
    if (!user) return;
    const colRef = collection(db, "users", user.uid, "events_favorited");

    const unsub = onSnapshot(colRef, (snap) => {
      const arr: FavDoc[] = snap.docs
        .map((d) => {
          const v = d.data() as any;

            const baseId = v.event?.id || v.eventId || d.id;
            const evt =
              v.event && typeof v.event === "object"
                ? { ...v.event, id: baseId }
                : { id: baseId };

            // Carry over start fields if present
            if (v.startTime != null && evt.startTime == null) evt.startTime = v.startTime;
            if (v.startTimeMs != null && evt.startTimeMs == null) evt.startTimeMs = v.startTimeMs;

            // Build best-effort Date & ms
            const salv = salvageStartDate({ ...evt, ...v });
            const startTimeMs =
              salv
                ? salv.getTime()
                : (typeof v.startTimeMs === "number" ? v.startTimeMs : null);

            // Ensure event object has real Date & ms
            if (salv) evt.startTime = salv;
            if (startTimeMs != null && evt.startTimeMs == null) evt.startTimeMs = startTimeMs;

            if (!evt.visibility) evt.visibility = "public";

            return {
              id: d.id,
              eventId: baseId,
              createdAt: v.createdAt,
              updatedAt: v.updatedAt,
              event: evt,
              startTimeMs,
            } as FavDoc;
        })
        .filter((f) => !!f.eventId);

      setFavDocs(arr);
    });

    return () => unsub();
  }, [readOnly, auth.currentUser]);

  // Hydrate favorites (enrich missing event data)
  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      if (!favDocs.length) {
        if (!cancelled) setFavResolved([]);
        return;
      }

      const needIds = new Set<string>();
      for (const f of favDocs) {
        const ev = f.event || {};
        const hasStart =
          !!(
            ev?.startTime &&
            (ev.startTime instanceof Date ||
              typeof ev.startTime === "string" ||
              typeof ev.startTime === "number")
          ) || typeof f.startTimeMs === "number";
        const lacksShape =
          !hasStart ||
          (!ev.visibility && !ev.format && !Array.isArray(ev.formatTypes));
        if (lacksShape) needIds.add(f.eventId);
      }

      const eventsById: Record<string, any> = {};
      const ids = Array.from(needIds);
      for (let i = 0; i < ids.length; i += 10) {
        const chunk = ids.slice(i, i + 10);
        try {
          const snap = await getDocs(
            query(collection(db, "events"), where("__name__", "in", chunk))
          );
          snap.forEach((d) => {
            eventsById[d.id] = { id: d.id, ...d.data() };
          });
        } catch {
          /* ignore private / missing */
        }
      }

      const enriched: FavDoc[] = favDocs.map((f) => {
        const base = eventsById[f.eventId]
          ? { ...eventsById[f.eventId] }
          : f.event || { id: f.eventId };

        const salv = salvageStartDate({ ...base, ...f });
        const startTimeMs = salv
          ? salv.getTime()
          : (typeof f.startTimeMs === "number" ? f.startTimeMs : null);

        const visibility = base.visibility || "public";
        const event = { ...base, id: f.eventId, visibility };

        // Ensure Date + ms on event
        if (salv) event.startTime = salv;
        if (startTimeMs != null && event.startTimeMs == null)
          event.startTimeMs = startTimeMs;

        return { ...f, event, startTimeMs };
      });

      if (!cancelled) setFavResolved(enriched);
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [favDocs]);

  // Boolean map for quick checks (keep using your optimistic toggle)
  useEffect(() => {
    const map: Record<string, boolean> = {};
    favResolved.forEach((f) => {
      map[f.eventId] = true;
    });
    setFavorites(map);
  }, [favResolved]);

  function onRequireAuth() {
    toast({
      title: "Sign in required",
      description: "Please sign in to manage favorites.",
      variant: "destructive",
    });
  }

  async function toggleFavoriteUI(eventId: string) {
    const user = auth.currentUser;
    if (!user) return onRequireAuth();
    const wasFav = !!favorites[eventId];

    // Optimistic UI
    setFavorites((prev) => {
      const n = { ...prev };
      if (wasFav) delete n[eventId];
      else n[eventId] = true;
      return n;
    });

    try {
      // Existing API
      await toggleFavoriteApi(eventId, wasFav);

      // Enforce server doc state to match intent
      const favRef = doc(db, "users", user.uid, "events_favorited", eventId);
      const snap = await getDoc(favRef);
      if (!wasFav) {
        if (!snap.exists()) {
          await setDoc(
            favRef,
            {
              eventId,
              event: { id: eventId },
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      } else {
        if (snap.exists()) {
          await deleteDoc(favRef);
        }
      }
    } catch (e) {
      // Rollback
      setFavorites((prev) => {
        const n = { ...prev };
        if (wasFav) n[eventId] = true;
        else delete n[eventId];
        return n;
      });
      toast({
        title: "Favorite failed",
        description: "Network or permission issue.",
        variant: "destructive",
      });
    }
  }

  async function removeFavoriteLocal(userUid: string, eventId: string) {
    try {
      await removeFavoriteApi(eventId);
      toast({ title: "Removed from Favorites", description: "The event was removed." });
    } catch (e: any) {
      toast({ title: "Failed to remove", description: e?.message || "Missing permission.", variant: "destructive" });
    }
  }

  async function saveFavorite(userUid: string, raw: any) {
    try {
      const eventId = String(raw.id);
      await addFavorite(eventId); // original helper
      // Ensure doc exists with expected fields
      await setDoc(
        doc(db, "users", userUid, "events_favorited", eventId),
        {
          eventId,
          event: { id: eventId, title: raw.title || raw.name || "Event" },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      toast({ title: "Saved to Favorites", description: `"${raw?.title || "Event"}" was added.` });
      return true;
    } catch (e: any) {
      const msg = String(e?.message || "");
      let description = "Missing permission or network error.";
      if (msg === "auth_required") description = "Please sign in to save events.";
      if (msg.includes("event_not_found")) description = "Event not found or private.";
      toast({ title: "Failed to save", description, variant: "destructive" });
      return false;
    }
  }

  /* --------------------------------- Filters --------------------------------- */

  function canonicalFormatName(raw: string): string {
    const lc = raw.toLowerCase().trim();
    return lc.charAt(0).toUpperCase() + lc.slice(1);
  }

  // Collect available formats (case-insensitive)
  const allFormats = useMemo(() => {
    const map = new Map<string, string>(); // lc -> display
    const seed = ["commander", "modern", "legacy", "standard", "pioneer", "pauper"];
    seed.forEach((f) => {
      const lc = f.toLowerCase();
      if (!map.has(lc)) map.set(lc, canonicalFormatName(f));
    });
    events.forEach((ev) => {
      const arr: any[] = Array.isArray(ev.formatTypes) ? ev.formatTypes : [];
      arr.forEach((f) => {
        if (typeof f === "string") {
          const lc = f.toLowerCase().trim();
          if (lc && !map.has(lc)) map.set(lc, canonicalFormatName(f));
        }
      });
      if (typeof (ev as any).format === "string") {
        const f = (ev as any).format;
        const lc = f.toLowerCase().trim();
        if (lc && !map.has(lc)) map.set(lc, canonicalFormatName(f));
      }
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([_, disp]) => disp);
  }, [events]);

  const myUid = auth.currentUser?.uid ?? null;

  // REMOVE obsolete local mode states introduced earlier
  // const [sectionMode, setSectionMode] = useState<"all"|"favorites"|"archived"|"created">("all");
  // const [layoutMode, setLayoutMode]   = useState<"swipe"|"carousel"|"grid">("grid");
  // const [calendarOpen, setCalendarOpen] = useState(false);

  // Simplified filter (tab-specific filtering handled later)
  const eventPassesFilters = useCallback((ev: any) => {
    if (visibilityFilter === "public" && ev.visibility !== "public") return false;
    if (visibilityFilter === "private" && ev.visibility !== "private") return false;

    if (selectedFormats.length) {
      const evFormats: string[] = Array.isArray(ev.formatTypes)
        ? ev.formatTypes
        : ev.format ? [ev.format] : [];
      const lower = evFormats.map(f => (f||"").toLowerCase());
      const needed = selectedFormats.map(f => f.toLowerCase());
      if (!needed.some(f => lower.includes(f))) return false;
    }

    if (lgsFilter !== "all") {
      const isLgs = !!(
        ev.organizerIsLgs ||
        (ev.organizerRankTitle || "").toLowerCase().includes("lgs") ||
        (ev.organizerAccountType || "").toLowerCase() === "lgs"
      );
      if (lgsFilter === "only" && !isLgs) return false;
      if (lgsFilter === "hide" && isLgs) return false;
    }

    if (selectedProfileUid) {
      const org = ev.organizerUid || ev.ownerUid;
      if (org !== selectedProfileUid) return false;
    }

    if (radiusMi != null && typeof ev.distanceMiles === "number" && ev.distanceMiles > radiusMi)
      return false;

    if (selectedDate) {
      const start = ev.startTime instanceof Date ? ev.startTime : new Date(ev.startTime);
      if (isNaN(start.getTime())) return false;
      if (
        start.getFullYear() !== selectedDate.getFullYear() ||
        start.getMonth() !== selectedDate.getMonth() ||
        start.getDate() !== selectedDate.getDate()
      ) return false;
    }

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      const hay = (
        (ev.title || "") + " " +
        (ev.organizerUsername || "") + " " +
        (Array.isArray(ev.formatTypes) ? ev.formatTypes.join(" ") : ev.format || "")
      ).toLowerCase();
      if (!hay.includes(q)) return false;
    }

    return true;
  }, [
    visibilityFilter,
    selectedFormats,
    lgsFilter,
    selectedProfileUid,
    radiusMi,
    selectedDate,
    searchTerm
  ]);

  // FIX: use events instead of nonexistent eventsByDate
  const visibleEvents = useMemo(
    () => events.filter(eventPassesFilters),
    [events, eventPassesFilters]
  );

  // If you previously referenced state 'visibleEvents', keep using this const (no setter).
  // Remove any effect that tried to sync URL and called setSectionMode or setLayoutMode on every render.
  // If you need initial URL sync, guard it:
  //
  // useEffect(() => {
  //   if (didInitRef.current) return;
  //   didInitRef.current = true;
  //   // perform one-time sync here
  // }, []);

  const nowMsFav = Date.now();
  const currentFavs = useMemo(
    () =>
      favResolved
        .filter((f) => {
          // If still missing startTimeMs after hydration, treat as upcoming so it appears.
          const t = typeof f.startTimeMs === "number" ? f.startTimeMs : Number.POSITIVE_INFINITY;
          return t >= nowMsFav;
        })
        .sort((a, b) => (a.startTimeMs ?? Number.POSITIVE_INFINITY) - (b.startTimeMs ?? Number.POSITIVE_INFINITY)),
    [favResolved, nowMsFav]
  );
  const pastFavs = useMemo(
    () =>
      favResolved
        .filter((f) => {
          const t = typeof f.startTimeMs === "number" ? f.startTimeMs : Number.POSITIVE_INFINITY;
          return t < nowMsFav;
        })
        .sort((a, b) => (b.startTimeMs ?? 0) - (a.startTimeMs ?? 0)),
    [favResolved, nowMsFav]
  );

  const filteredFavorites = useMemo(() => currentFavs.map((f) => f.event).filter(eventPassesFilters), [currentFavs, eventPassesFilters]);
  const filteredPastFavorites = useMemo(() => pastFavs.map((f) => f.event).filter(eventPassesFilters), [pastFavs, eventPassesFilters]);
  const filteredCreated = useMemo(
    () =>
      events
        .filter((e) => (e.organizerUid || e.ownerUid) === myUid)
        .filter(eventPassesFilters),
    [events, myUid, eventPassesFilters]
  );

  // Source per tab (already filtered)
  const deckEvents = useMemo(() => {
    if (tab === "favorites") return filteredFavorites;
    if (tab === "archive") return filteredPastFavorites;
    if (tab === "created") return filteredCreated;
    return visibleEvents;
  }, [tab, filteredFavorites, filteredPastFavorites, filteredCreated, visibleEvents]);

  // Keep ordered deck aligned with current tab list
  useEffect(() => {
    setOrdered((prev) => {
      if (!prev.length) return deckEvents;
      const cur = prev[idx];
      if (cur && deckEvents.some((e) => e.id === cur.id)) {
        return [cur, ...deckEvents.filter((e) => e.id !== cur.id)];
      }
      setIdx(0);
      return deckEvents;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckEvents]);

  /* ------------------------------- Grid helpers ------------------------------ */

  const PAGE_CHUNK = 60;
  const pagedList = useMemo(() => deckEvents.slice(0, PAGE_CHUNK + (hasMore ? 0 : 0)), [deckEvents, hasMore]); // simple slice; sentinel loads more by refetch

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e.isIntersecting && !loadingPage && (viewMode === "grid" || viewMode === "carousel")) {
          // Simple strategy: on scroll end, trigger a refresh (server-side ordering keeps things stable).
          setLoadingPage(true);
          setTimeout(() => setLoadingPage(false), 350);
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadingPage, viewMode]);

  /* ------------------------------ Card rendering ----------------------------- */

  const [editing, setEditing] = useState<any | null>(null);

  function renderEventCard(raw: any, extra: Partial<React.ComponentProps<typeof EventCard>> = {}) {
    const evt = normalizeForCard(raw);
    if (!evt || !evt.id) return null;

    // Belt & suspenders: reconstruct Date from startTimeMs if missing
    if (!evt.startTime && (raw as any)?.startTimeMs) {
      const ms = Number((raw as any).startTimeMs);
      if (Number.isFinite(ms)) evt.startTime = new Date(ms);
    }

    if (!evt.startTime || !(evt.startTime instanceof Date) || isNaN(evt.startTime.getTime())) {
      const salv = salvageStartDate(raw);
      if (salv) evt.startTime = salv;
      else {
        console.warn("[events] skipping event (no valid startTime)", evt.id, raw?.startTime);
        return null;
      }
    }
    const isFavorite = !!favorites[evt.id];
    const canManage = !!(auth.currentUser?.uid && evt.organizerUid === auth.currentUser.uid);
    return (
      <EventCard
        key={evt.id}
        ev={evt}
        isFavorite={isFavorite}
        canManage={canManage}
        onEdit={(e) => setEditing(e)}
        onDelete={async (e) => {
          const ok = await deleteEventCascade(e.id);
            if (ok) {
              setEvents((prev) => prev.filter((x) => x.id !== e.id));
              toast({ title: "Deleted", description: "Event removed." });
            } else {
              toast({
                title: "Delete failed",
                description: "Permission or network issue.",
                variant: "destructive",
              });
            }
        }}
        {...(extra as any)}
      />
    );
  }

  function renderGrid(list: any[], emptyMessage: string) {
    if (!list.length) {
      return <div className="p-6 text-center text-muted-foreground">{emptyMessage}</div>;
    }
    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-3 pt-3">
          {list.map((e) => renderEventCard(e))}
        </div>
        <div ref={sentinelRef} className="h-10" />
        {loadingPage && <div className="py-3 text-center text-xs text-muted-foreground">Loading…</div>}
      </>
    );
  }

  // Simple carousel (one card at a time, arrow nav, not the swipe deck)
  function rotateForward(markView = true) {
    if (!ordered.length) return;
    const cur = ordered[idx];
    const nextDeck = [...ordered];
    nextDeck.splice(idx, 1);
    nextDeck.push(cur);
    setOrdered(nextDeck);
    if (markView && cur?.id) {
      setViewed((s) => {
        const n = new Set(s);
        n.add(cur.id);
        return n;
      });
    }
    setIdx(0);
  }
  function rotateBackward(markView = true) {
    if (!ordered.length) return;
    const last = ordered[ordered.length - 1];
    setOrdered([last, ...ordered.slice(0, -1)]);
    if (markView && last?.id) {
      setViewed((s) => {
        const n = new Set(s);
        n.add(last.id);
        return n;
      });
    }
    setIdx(0);
  }

  function renderDeck(list: any[], emptyMessage: string) {
    if (!list.length) {
      return <div className="p-6 text-center text-muted-foreground">{emptyMessage}</div>;
    }
    const current = ordered[idx] || list[0];
    return (
      <div id="events-deck-area" className="relative flex flex-col items-center gap-6 px-4 pb-10 w-full">
        {list.length > 1 && (
          <button
            aria-label="Previous"
            className="absolute left-1 top-1/2 -translate-y-1/2 z-30 bg-slate-800/70 hover:bg-slate-700 text-white rounded-full w-9 h-9 flex items-center justify-center shadow"
            onClick={() => rotateBackward()}
            type="button"
          >
            ‹
          </button>
        )}
        {list.length > 1 && (
          <button
            aria-label="Next"
            className="absolute right-1 top-1/2 -translate-y-1/2 z-30 bg-slate-800/70 hover:bg-slate-700 text-white rounded-full w-9 h-9 flex items-center justify-center shadow"
            onClick={() => rotateForward()}
            type="button"
          >
            ›
          </button>
        )}

        <div id="events-active-card" className="w-full max-w-xl touch-pan-y select-none relative rounded-lg">
          {current && renderEventCard(current, { style: { willChange: "transform" } } as any)}
        </div>

        {list.length > 1 && (
          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
            {list.findIndex((e) => e.id === current?.id) + 1} / {list.length}
          </div>
        )}
      </div>
    );
  }

  // Helper to map any list of raw events to SwipeDeck's EventDoc shape
  const toSwipeDocs = (base: any[]): EventDoc[] => {
    const toDate = (x: any) =>
      x?.toDate ? x.toDate() : typeof x === "number" ? new Date(x) : x instanceof Date ? x : x ? new Date(x) : new Date();

    return base.map((raw) => {
      const start = raw.startTime instanceof Date ? raw.startTime : toDate(raw.startTime);
      return {
        id: String(raw.id),
        title: raw.title || "Untitled Event",
        details: raw.details || "",
        formatTypes: Array.isArray(raw.formatTypes) ? raw.formatTypes : [],
        visibility: raw.visibility === "private" ? "private" : "public",
        startTime: start || new Date(),
        endTime: raw.endTime ? toDate(raw.endTime) : null,
        organizerUid: raw.organizerUid || raw.ownerUid || "",
        organizerUsername: raw.organizerUsername || "player",
        organizerAvatarUrl: raw.organizerAvatarUrl || null,
        organizerIsLGS: !!raw.organizerIsLGS,
        images: Array.isArray(raw.images) ? raw.images : [],
        distanceMiles: typeof raw.distanceMiles === "number" ? raw.distanceMiles : null,
        favoritesCount: typeof raw.favoritesCount === "number" ? raw.favoritesCount : 0,
      };
    });
  };

  // Swipe sources for each tab
  const swipeAll: EventDoc[] = useMemo(() => toSwipeDocs(deckEvents), [deckEvents]);
  const swipeFavs: EventDoc[] = useMemo(() => toSwipeDocs(filteredFavorites), [filteredFavorites]);
  const swipeArchived: EventDoc[] = useMemo(() => toSwipeDocs(filteredPastFavorites), [filteredPastFavorites]);
  const swipeCreated: EventDoc[] = useMemo(() => toSwipeDocs(filteredCreated), [filteredCreated]);

  async function recordSwipe(direction: "left" | "right", ev: EventDoc) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
      const bucket = direction === "right" ? "events_interested" : "events_passed";
      await setDoc(
        doc(db, "users", uid, bucket, ev.id),
        { eid: ev.id, at: serverTimestamp(), vis: ev.visibility },
        { merge: true }
      );
      if (direction === "right") {
        // Also optimistic favorite toggle
        await toggleFavoriteUI(ev.id);
      }
    } catch {
      // ignore
    }
  }

  /* --------------------------------- Toolbar -------------------------------- */

  function handleRefresh() {
    setRefreshKey((k) => k + 1);
  }

  const envWarn =
    !userLatLng ? (
      <div className="text-xs text-amber-300 px-4 py-2">
        Add your location in <a href="/settings" className="underline">Settings</a> (or allow location) to sort nearby events first.
      </div>
    ) : null;

  return (
    <div className="flex flex-col h-full">
      {/* Top Toolbar */}
      <div className="flex flex-col gap-2 px-4 py-3 border-b border-white/10 bg-slate-900/60">
        {/* Row: layout + sections + calendar toggle */}
        <div className="flex flex-wrap items-center gap-2">
          {(["swipe","carousel","grid"] as const).map(m => {
            const active = viewMode === m;
            return (
              <button
                key={m}
                onClick={() => setViewMode(m)}
                className={
                  "text-[11px] px-3 py-1 rounded-full border transition " +
                  (active
                    ? "bg-white/15 border-white/40 text-white"
                    : "bg-black/40 border-white/15 text-white/60 hover:bg-white/10")
                }
              >
                {m[0].toUpperCase()+m.slice(1)}
              </button>
            );
          })}
          <div className="mx-2 h-5 w-px bg-white/10" />
        </div>

        {/* Collapsible calendar (month-year collapsed state) */}
        {selectedDate && (
          <div className="text-[11px] text-white/50">
            Date filter: {selectedDate.toLocaleDateString()}
          </div>
        )}
      </div>

      {envWarn}

      {/* Body */}
      <div className="flex-1 overflow-hidden">
        <Tabs
          value={tab}
          className="h-full"
          onValueChange={(v) => {
            setTab(v as any);
            updateUrl({ tab: v });
          }}
        >
          <TabsList className="px-4 py-2">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="favorites" disabled={readOnly}>
              Favorites
            </TabsTrigger>
            <TabsTrigger value="archive" disabled={readOnly}>
              Archived
            </TabsTrigger>
            <TabsTrigger value="created">Created</TabsTrigger>
          </TabsList>

          {/* All */}
          <TabsContent value="all" className="h-full overflow-auto">
            {viewMode === "swipe" ? (
              swipeAll.length ? (
                <div className="px-2 pb-8">
                  <SwipeDeck
                    items={swipeAll}
                    onRefresh={handleRefresh}
                    onSwiped={({ direction, item }) => {
                      void recordSwipe(direction, item as any);
                    }}
                    renderCard={(ev: EventDoc) => renderEventCard(ev)}
                  />
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  No events to swipe. Adjust filters.
                </div>
              )
            ) : viewMode === "carousel" ? (
              renderDeck(visibleEvents, "No events found. Create one or adjust filters.")
            ) : (
              renderGrid(visibleEvents, "No events found. Create one or adjust filters.")
            )}
          </TabsContent>

          {/* Favorites (upcoming) */}
          <TabsContent value="favorites" className="h-full overflow-auto">
            {viewMode === "swipe" ? (
              swipeFavs.length ? (
                <div className="px-2 pb-8">
                  <SwipeDeck
                    items={swipeFavs}
                    onRefresh={handleRefresh}
                    onSwiped={({ direction, item }) => {
                      // Only record if actually in favorites list
                      if (favorites[item.id]) void recordSwipe(direction, item as any);
                    }}
                    renderCard={(ev: EventDoc) => renderEventCard(ev)}
                  />
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  No upcoming favorites to swipe.
                </div>
              )
            ) : viewMode === "carousel" ? (
              renderDeck(filteredFavorites, "No upcoming favorites yet.")
            ) : (
              renderGrid(filteredFavorites, "No upcoming favorites yet.")
            )}
          </TabsContent>

          {/* Archived favorites (past) */}
          <TabsContent value="archive" className="h-full overflow-auto">
            {viewMode === "swipe" ? (
              swipeArchived.length ? (
                <div className="px-2 pb-8">
                  <SwipeDeck
                    items={swipeArchived}
                    onRefresh={handleRefresh}
                    onSwiped={({ direction, item }) => {
                      // archive swipes are informational; record left/right if desired
                      void recordSwipe(direction, item as any);
                    }}
                    renderCard={(ev: EventDoc) => renderEventCard(ev)}
                  />
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  No archived favorites.
                </div>
              )
            ) : viewMode === "carousel" ? (
              renderDeck(filteredPastFavorites, "No archived favorites.")
            ) : (
              renderGrid(filteredPastFavorites, "No archived favorites.")
            )}
          </TabsContent>

          {/* Created by me */}
          <TabsContent value="created" className="h-full overflow-auto">
            {!readOnly && (
              <div className="px-4 pt-4">
                <Button size="sm" onClick={() => setEditing({ mode: "create" })} className="mb-2">
                  + Create Event
                </Button>
              </div>
            )}
            {viewMode === "swipe" ? (
              swipeCreated.length ? (
                <div className="px-2 pb-8">
                  <SwipeDeck
                    items={swipeCreated}
                    onRefresh={handleRefresh}
                    onSwiped={({ direction, item }) => {
                      void recordSwipe(direction, item as any);
                    }}
                    renderCard={(ev: EventDoc) => renderEventCard(ev)}
                  />
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  You have not created any events.
                </div>
              )
            ) : viewMode === "carousel" ? (
              renderDeck(filteredCreated, "You have not created any events.")
            ) : (
              renderGrid(filteredCreated, "You have not created any events.")
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialogs */}
      {!readOnly && editing?.mode === "create" && (
        <CreateEventDialog
          open={true}
          onOpenChange={(v) => !v && setEditing(null)}
          mode="create"
          onSaved={(created) => {
            setEditing(null);
            if (created?.length) {
              setEvents((prev) => {
                const map = new Map(prev.map((e) => [e.id, e]));
                created.forEach((c) => map.set(c.id, { ...(map.get(c.id) || {}), ...c }));
                return Array.from(map.values());
              });
            } else {
              setRefreshKey((k) => k + 1);
            }
          }}
        />
      )}
      {!readOnly && editing && editing.mode !== "create" && (
        <CreateEventDialog
          open={!!editing}
          onOpenChange={(v) => {
            if (!v) setEditing(null);
          }}
          mode="edit"
          initialEvent={editing}
          eventId={editing.id}
          onSaved={(updated) => {
            setEditing(null);
            if (updated?.length) {
              setEvents((prev) => prev.map((e) => (e.id === updated[0].id ? { ...e, ...updated[0] } : e)));
            } else {
              setRefreshKey((k) => k + 1);
            }
          }}
        />
      )}
    </div>
  );
}

/* --------------------------- Delete helpers (safe) --------------------------- */

async function deleteEventCascade(eid: string) {
  try {
    await deleteDoc(doc(db, "events", eid));
    return true;
  } catch (e) {
    console.error("[events] delete failed", e);
    return false;
  }
}

function InlineMiniMonth({ selected, onSelect }: { selected: Date | null; onSelect: (d: Date|null)=>void }) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const first = new Date(y, m, 1);
  const start = first.getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells: (Date|null)[] = [];
  for (let i=0;i<start;i++) cells.push(null);
  for (let d=1; d<=days; d++) cells.push(new Date(y,m,d));
  return (
    <div className="inline-block rounded-md border border-white/10 bg-black/50 p-2">
      <div className="text-xs font-medium text-white/70 mb-1">
        {today.toLocaleString(undefined,{month:"long",year:"numeric"})}
      </div>
      <div className="grid grid-cols-7 gap-[2px] text-[10px] text-white/40 mb-1">
        {["S","M","T","W","T","F","S"].map((d,i)=><div key={i} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-[3px]">
        {cells.map((d,i)=>{
          if(!d) return <div key={i} className="h-6" />;
          const isSel = selected && d.toDateString()===selected.toDateString();
          const isToday = d.toDateString()===today.toDateString();
          return (
            <button
              key={i}
              onClick={()=>onSelect(isSel ? null : d)}
              className={
                "h-6 w-6 flex items-center justify-center rounded text-[11px] border transition " +
                (isSel
                  ? "bg-amber-500/40 border-amber-400/70 text-amber-100"
                  : isToday
                  ? "bg-white/15 border-white/40 text-white"
                  : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10")
              }
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}


